import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Pagination,
  Table,
  Tooltip as FBTooltip,
  Modal,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { IoMdSearch } from "react-icons/io";
import TableLoading from "../TableLoading";
import { Tooltip } from "react-tooltip";
import { MdEdit, MdOutlineWhatsapp } from "react-icons/md";
import { IoBarChartSharp } from "react-icons/io5";
import ViewProviderForm from "../forms/ViewProviderForm";
import ViewProviderUsage from "../forms/ViewProviderUsage";
import * as XLSX from "xlsx";
import { MdFileDownload } from "react-icons/md";

const ITEMS_PER_PAGE = 20;

const SORTS = {
  NAME_ASC: "name:asc",
  NAME_DESC: "name:desc",
  COUNT_ASC: "count:asc",
  COUNT_DESC: "count:desc",
};

function flattenProvidersToRows(providers = []) {
  const rows = [];
  for (const prov of providers) {
    const base = {
      providerName: prov?.name ?? "",
    };

    if (
      Array.isArray(prov?.associatedQueries) &&
      prov.associatedQueries.length
    ) {
      for (const q of prov.associatedQueries) {
        rows.push({
          ...base,
          productName: q?.name ?? "",
          price:
            q?.QueryProvider?.price != null
              ? Number(q.QueryProvider.price)
              : null,
        });
      }
    } else {
      // Se quiser incluir provedores sem produto, descomente a linha abaixo
      // rows.push({ ...base, productId: "", productName: "", categoryId: "", price: null });
    }
  }
  return rows;
}

async function fetchAllProvidersForExport({ axios, search, sort }) {
  // Busca todas as páginas respeitando os filtros atuais
  const itemsPerPage = 500; // grande para minimizar requisições
  let page = 1;
  let all = [];
  let totalPages = 1;

  do {
    const { data } = await axios.get(
      `${ADMIN.GET_PROVIDERS}?page=${page}` +
        `&searchName=${encodeURIComponent(search || "")}` +
        `&sort=${encodeURIComponent(sort || "")}` +
        `&itemsPerPage=${itemsPerPage}`
    );
    const payload = data?.data || data;
    const list = payload?.data || payload || [];
    totalPages = payload?.totalPages || 1;
    all = all.concat(list || []);
    page += 1;
  } while (page <= totalPages);

  return all;
}

const AdminProvidersTable = () => {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [sort, setSort] = useState(SORTS.NAME_ASC);

  // estados dos modais
  const [isModalViewProvider, setIsModalViewProvider] = useState(false);
  const [isModalViewUsage, setIsModalViewUsage] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const onPageChange = (p) => setCurrentPage(p);

  const {
    isLoading: isLoadingProviders,
    error: errorListProviders,
    data: listProviders,
  } = useQuery(
    ["listProviders", currentPage, search, sort],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_PROVIDERS}?page=${currentPage}` +
          `&searchName=${encodeURIComponent(search)}` +
          `&sort=${encodeURIComponent(sort)}` +
          `&itemsPerPage=${ITEMS_PER_PAGE}`
      );
      setTotalPages(data?.data?.totalPages || 1);
      setTotalCount(data?.data?.totalCount || 0);
      return data?.data?.data || data?.data || [];
    },
    { keepPreviousData: true }
  );

  const handleSubmitSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const handleViewProvider = (provider) => {
    setSelectedProvider(provider);
    setIsModalViewProvider(true);
  };

  const handleViewUsage = (provider) => {
    setSelectedProvider(provider);
    setIsModalViewUsage(true);
  };

  const headerSubtitle = useMemo(() => {
    if (isLoadingProviders) return "Carregando provedores…";
    if (!listProviders?.length) return "Nenhum provedor encontrado";
    return `${totalCount} ${totalCount === 1 ? "registro" : "registros"}`;
  }, [isLoadingProviders, listProviders, totalCount]);

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);

      // 1) Buscar tudo (todas as páginas) com os filtros atuais
      const providersAll = await fetchAllProvidersForExport({
        axios,
        search,
        sort,
      });

      // 2) Achatar para linhas
      const rows = flattenProvidersToRows(providersAll);

      if (!rows.length) {
        alert("Nenhum produto encontrado para exportar.");
        return;
      }

      // 3) Criar workbook/worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ["providerName", "productName", "price"],
      });

      // Larguras amigáveis (opcional)
      ws["!cols"] = [
        { wch: 28 }, // providerName
        { wch: 36 }, // productName
        { wch: 10 }, // price
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Produtos_por_Provedor");

      // 4) Salvar arquivo
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const y = now.getFullYear();
      const m = pad(now.getMonth() + 1);
      const d = pad(now.getDate());
      XLSX.writeFile(wb, `providers_produtos_precos_${y}-${m}-${d}.xlsx`);
    } catch (e) {
      console.error(e);
      alert("Não foi possível gerar o Excel. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            Provedores
          </h2>
          <p className="text-xs text-gray-500">{headerSubtitle}</p>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <form
            onSubmit={handleSubmitSearch}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1"
          >
            <IoMdSearch className="text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por nome…"
              className="w-52 sm:w-64 border-0 p-0 text-sm focus:ring-0"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button size="xs" color="gray" type="submit">
              Buscar
            </Button>
          </form>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Ordenar:</label>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setCurrentPage(1);
              }}
              className="rounded-md border-gray-300 text-sm"
            >
              <option value={SORTS.NAME_ASC}>Nome (A–Z)</option>
              <option value={SORTS.NAME_DESC}>Nome (Z–A)</option>
              <option value={SORTS.COUNT_DESC}>Qtd. Insumos (↓)</option>
              <option value={SORTS.COUNT_ASC}>Qtd. Insumos (↑)</option>
            </select>
          </div>
          <Button
            size="xs"
            color="success"
            onClick={handleExportExcel}
            isProcessing={isExporting}
            disabled={isExporting}
            className="flex items-center gap-2"
            title="Exportar todos os produtos e preços (respeitando filtros)"
          >
            <MdFileDownload className="text-lg" />
            <span>Exportar Excel</span>
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table
          striped
          hoverable
          className="text-xs xl:text-sm"
          theme={{
            root: { base: "w-full text-left", shadow: "" },
            body: { cell: { base: "px-3 py-2 align-middle" } },
            head: {
              base: "bg-gray-100 text-gray-700 z-10 text-xs",
              cell: {
                base: "px-3 py-2 font-medium text-gray-700 whitespace-nowrap",
              },
            },
            row: {
              base: "bg-white hover:bg-indigo-50/40 focus-within:bg-indigo-50/40",
            },
          }}
        >
          {/* Head */}
          {listProviders?.length > 0 || isLoadingProviders ? (
            <Table.Head>
              <Table.HeadCell className="w-[28%] min-w-[220px]">
                Provedor
              </Table.HeadCell>
              <Table.HeadCell className="w-[24%] text-center">
                Responsável
              </Table.HeadCell>
              <Table.HeadCell className="w-[18%] text-center">
                Telefone
              </Table.HeadCell>
              <Table.HeadCell className="w-[12%] text-center">
                Qtd. Insumos
              </Table.HeadCell>
              <Table.HeadCell className="w-[18%] text-center">
                Ações
              </Table.HeadCell>
            </Table.Head>
          ) : null}

          {/* Body */}
          <Table.Body className="divide-y">
            {!isLoadingProviders ? (
              listProviders?.length ? (
                listProviders.map((provider) => {
                  const count = provider?.associatedQueries?.length ?? 0;
                  const rowId = `prov-${provider.id}`;
                  return (
                    <Table.Row key={provider.id}>
                      <Table.Cell className="font-medium text-gray-900">
                        <div
                          className="truncate max-w-[360px]"
                          title={`${provider?.id ?? ""} - ${
                            provider?.name ?? ""
                          }`}
                        >
                          {provider?.id} — {provider?.name}
                        </div>
                      </Table.Cell>

                      <Table.Cell className="text-center">
                        <span
                          className="inline-block max-w-[240px] truncate"
                          title={provider?.responsibleName || "Não informado"}
                        >
                          {provider?.responsibleName || "Não informado"}
                        </span>
                      </Table.Cell>

                      <Table.Cell className="text-center">
                        {provider?.phoneNumber ? (
                          <a
                            href={`https://wa.me/55${provider?.phoneNumber?.replace(
                              /\D/g,
                              ""
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 hover:underline"
                            title="Abrir WhatsApp"
                          >
                            {provider.phoneNumber}
                          </a>
                        ) : (
                          <span className="text-gray-400">Não informado</span>
                        )}
                      </Table.Cell>

                      <Table.Cell className="text-center">
                        <span className="inline-flex items-center justify-center min-w-[3ch] rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                          {count}
                        </span>
                      </Table.Cell>

                      <Table.Cell className="text-center">
                        <div className="flex items-center justify-center gap-3">
                          <Tooltip
                            id={`${rowId}-view`}
                            content="Editar Informações"
                          />
                          <MdEdit
                            data-tooltip-id={`${rowId}-view`}
                            fontSize="18"
                            onClick={() => handleViewProvider(provider)}
                            className="cursor-pointer text-gray-600 hover:text-gray-900"
                          />

                          <Tooltip
                            id={`${rowId}-usage`}
                            content="Visualizar consumo"
                          />
                          <IoBarChartSharp
                            data-tooltip-id={`${rowId}-usage`}
                            fontSize="18"
                            onClick={() => handleViewUsage(provider)}
                            className="cursor-pointer text-gray-600 hover:text-gray-900"
                          />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={5} className="py-10">
                    <div className="flex flex-col items-center justify-center gap-1 text-gray-500">
                      <span className="text-sm">
                        Nenhum provedor encontrado.
                      </span>
                      {search ? (
                        <span className="text-xs">
                          Tente limpar ou alterar a busca.
                        </span>
                      ) : null}
                    </div>
                  </Table.Cell>
                </Table.Row>
              )
            ) : (
              <TableLoading cols={5} />
            )}
          </Table.Body>
        </Table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 border-t bg-gray-50 px-3 py-2">
        <div className="text-xs text-gray-600">
          Exibindo{" "}
          {totalCount > 0
            ? `${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(
                currentPage * ITEMS_PER_PAGE,
                totalCount
              )}`
            : "0"}{" "}
          de {totalCount}
        </div>
        {totalPages > 1 && (
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        )}
      </div>

      {/* MODAL: Detalhes */}
      <Modal
        show={isModalViewProvider}
        onClose={() => setIsModalViewProvider(false)}
        size="5xl"
        dismissible
      >
        <Modal.Header>Visualizar provedor</Modal.Header>
        <Modal.Body>
          <ViewProviderForm
            provider={selectedProvider}
            onClose={() => setIsModalViewProvider(false)}
            fetchProviders={() => {
              queryClient.invalidateQueries([
                "listProviders",
                currentPage,
                search,
                sort,
              ]);
            }}
          />
        </Modal.Body>
      </Modal>

      {/* MODAL: Consumo */}
      <Modal
        show={isModalViewUsage}
        onClose={() => setIsModalViewUsage(false)}
        size="5xl"
        dismissible
      >
        <Modal.Header>Visualizar relatório de consumo</Modal.Header>
        <Modal.Body>
          <ViewProviderUsage
            provider={selectedProvider}
            onClose={() => setIsModalViewUsage(false)}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminProvidersTable;
